import { SessionTaskAttemptResultEnum } from "~/lib/objects";

// Row key strings as defined in step 8
export type DownloadRowKey =
  | "pending"
  | "draw"
  | "loss"
  | "external-hint"
  | "first-try-solved"
  | "retry-group"
  | `retry-${number}-${"win" | "draw" | "external-hint" | "loss"}`;

export interface DownloadTaskItem {
  problemId: number;
  name: string;
}

export interface DownloadRowModel {
  key: DownloadRowKey;
  label: string;
  tasks: DownloadTaskItem[];
  children?: DownloadRowModel[];
}

type DownloadProblemInput = {
  problemId: number;
  name?: string | null;
  title?: string | null;
  problemOrder?: number | null;
};

type DownloadAttemptInput = {
  problemId: number;
  finalResult?: SessionTaskAttemptResultEnum | string;
  retryCountUsed?: number;
  finalExternalHint?: boolean;
  mainAttemptId?: number | null;
};

// Helper to classify an individual attempt for retry child key
function classifyAttemptType(attempt: {
  finalResult?: SessionTaskAttemptResultEnum | string;
  finalExternalHint?: boolean;
}): "win" | "draw" | "loss" | "external-hint" {
  if (attempt.finalExternalHint) return "external-hint";
  if (attempt.finalResult === SessionTaskAttemptResultEnum.DRAW) return "draw";
  if (attempt.finalResult === SessionTaskAttemptResultEnum.LOSS) return "loss";
  // default to win for anything else (including WIN/null/undefined)
  return "win";
}

function isCompletedResult(
  finalResult: SessionTaskAttemptResultEnum | string | undefined,
): boolean {
  return (
    finalResult === SessionTaskAttemptResultEnum.WIN ||
    finalResult === SessionTaskAttemptResultEnum.DRAW ||
    finalResult === SessionTaskAttemptResultEnum.LOSS
  );
}

function isMainAttempt(attempt: DownloadAttemptInput): boolean {
  return (
    attempt.mainAttemptId === undefined ||
    attempt.mainAttemptId === null ||
    attempt.mainAttemptId === 0
  );
}

// Build the static rows with retry children dynamically computed
export function buildDownloadRows(
  problems: DownloadProblemInput[] = [],
  attempts: DownloadAttemptInput[] = [],
): DownloadRowModel[] {
  const problemIndex = new Map<number, number>();
  for (let i = 0; i < problems.length; i += 1) {
    problemIndex.set(problems[i]!.problemId, i);
  }

  const sortedProblems = [...problems].sort((a, b) => {
    const aOrder =
      typeof a.problemOrder === "number"
        ? a.problemOrder
        : Number.MAX_SAFE_INTEGER;
    const bOrder =
      typeof b.problemOrder === "number"
        ? b.problemOrder
        : Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (
      (problemIndex.get(a.problemId) ?? 0) -
      (problemIndex.get(b.problemId) ?? 0)
    );
  });

  const mainCompletedAttempts = new Map<number, DownloadAttemptInput>();
  for (const attempt of attempts ?? []) {
    if (!isMainAttempt(attempt)) continue;
    if (!isCompletedResult(attempt.finalResult)) continue;
    mainCompletedAttempts.set(attempt.problemId, attempt);
  }

  const topLevelTasks = new Map<DownloadRowKey, DownloadTaskItem[]>([
    ["pending", []],
    ["draw", []],
    ["loss", []],
    ["external-hint", []],
    ["first-try-solved", []],
    ["retry-group", []],
  ]);

  // compute retry child keys from tasks/attempts
  const retryKeySet = new Set<DownloadRowKey>();
  const retryChildTasks = new Map<DownloadRowKey, DownloadTaskItem[]>();

  for (const problem of sortedProblems) {
    const problemTask: DownloadTaskItem = {
      problemId: problem.problemId,
      name: problem.name ?? problem.title ?? String(problem.problemId),
    };
    const attempt = mainCompletedAttempts.get(problem.problemId);

    if (!attempt) {
      topLevelTasks.get("pending")!.push(problemTask);
      continue;
    }

    const retryCount =
      typeof attempt.retryCountUsed === "number" ? attempt.retryCountUsed : 0;

    if (retryCount > 0) {
      const type = classifyAttemptType(attempt);
      const key = `retry-${retryCount}-${type}` as DownloadRowKey;
      retryKeySet.add(key);
      topLevelTasks.get("retry-group")!.push(problemTask);
      if (!retryChildTasks.has(key)) {
        retryChildTasks.set(key, []);
      }
      retryChildTasks.get(key)!.push(problemTask);
      continue;
    }

    if (attempt.finalExternalHint) {
      topLevelTasks.get("external-hint")!.push(problemTask);
      continue;
    }

    if (attempt.finalResult === SessionTaskAttemptResultEnum.DRAW) {
      topLevelTasks.get("draw")!.push(problemTask);
      continue;
    }

    if (attempt.finalResult === SessionTaskAttemptResultEnum.LOSS) {
      topLevelTasks.get("loss")!.push(problemTask);
      continue;
    }

    if (attempt.finalResult === SessionTaskAttemptResultEnum.WIN) {
      topLevelTasks.get("first-try-solved")!.push(problemTask);
      continue;
    }

    topLevelTasks.get("pending")!.push(problemTask);
  }

  // sort retry children by retry count asc, then type order
  const typeOrder: Record<string, number> = {
    win: 0,
    draw: 1,
    "external-hint": 2,
    loss: 3,
  };

  const retryChildren: DownloadRowModel[] = Array.from(retryKeySet)
    .sort((a, b) => {
      const matchA = a.match(/^retry-(\d+)-(.*)$/)!;
      const matchB = b.match(/^retry-(\d+)-(.*)$/)!;
      const aCount = Number(matchA[1]);
      const bCount = Number(matchB[1]);
      if (aCount !== bCount) return aCount - bCount;
      const aType = matchA[2] ?? "";
      const bType = matchB[2] ?? "";
      return (typeOrder[aType] ?? 0) - (typeOrder[bType] ?? 0);
    })
    .map((key) => ({
      key,
      label: key,
      tasks: retryChildTasks.get(key) ?? [],
    }));

  const rows: DownloadRowModel[] = [
    { key: "pending", label: "pending", tasks: topLevelTasks.get("pending")! },
    { key: "draw", label: "draw", tasks: topLevelTasks.get("draw")! },
    { key: "loss", label: "loss", tasks: topLevelTasks.get("loss")! },
    {
      key: "external-hint",
      label: "external-hint",
      tasks: topLevelTasks.get("external-hint")!,
    },
    {
      key: "first-try-solved",
      label: "first-try-solved",
      tasks: topLevelTasks.get("first-try-solved")!,
    },
    {
      key: "retry-group",
      label: "retry-group",
      tasks: topLevelTasks.get("retry-group")!,
      children: retryChildren,
    },
  ];

  return rows;
}

export function buildDefaultDownloadSelection(
  rows: DownloadRowModel[],
): Record<DownloadRowKey, boolean> {
  const sel: Partial<Record<DownloadRowKey, boolean>> = {};

  for (const row of rows) {
    // By default every available category is selected except the
    // "first-try-solved" bucket which should start unchecked.  Retry
    // children remain true so that parent cascades still work normally.
    if (row.key === "first-try-solved") {
      sel[row.key] = false;
    } else {
      sel[row.key] = true;
    }

    if (row.children) {
      for (const child of row.children) {
        sel[child.key] = true;
      }
    }
  }

  return sel as Record<DownloadRowKey, boolean>;
}

export function getRetryChildKeys(rows: DownloadRowModel[]): DownloadRowKey[] {
  const keys: DownloadRowKey[] = [];
  for (const row of rows) {
    if (row.key === "retry-group" && row.children) {
      for (const child of row.children) {
        keys.push(child.key);
      }
    }
  }
  return keys;
}
